import React from 'react';
import PropTypes from 'prop-types';
import ConfigStepLayout from '../../components/Layout/ConfigStepLayout';
import WidgetEditor from '../../editors/WidgetEditor/WidgetEditor';
import IntlMessages from 'util/IntlMessages';

const SCREEN_LABELS = {
  results: 'search.editor.screen.results',
  empty: 'search.editor.screen.empty',
  noResults: 'search.editor.screen.noResults',
  no_results: 'search.editor.screen.noResults',
};

const DEFAULT_SCREEN_VALUES = ['results', 'empty'];

const getPreferredScreen = (screenValues) => {
  if (!Array.isArray(screenValues) || screenValues.length === 0) return null;
  return screenValues.includes('results') ? 'results' : screenValues[0];
};

const ConfigStep = ({
  onReady,
  onRegisterSave,
  onRegisterReload,
  onUpdate,
  shopId,
  client,
  view = 'searchBox',
  experienceType,
  stepId,
  title,
  description,
  isExpanded,
  onExpandToggle,
}) => {
  const handleConfigChange = React.useCallback(() => {
    if (onUpdate) {
      onUpdate({
        [stepId || view]: {
          changed: true,
          timestamp: Date.now(),
        },
      });
    }
  }, [onUpdate, stepId, view]);

  const editor = WidgetEditor({
    shopId,
    client,
    view,
    experienceType,
    onReady,
    onConfigChange: handleConfigChange,
  });

  const registeredSave = editor && typeof editor === 'object' && editor.controls
    ? editor.controls.saveConfig
    : null;
  const registeredReload = editor && typeof editor === 'object' && editor.controls
    ? editor.controls.reloadConfig
    : null;
  const editorConfig = editor && typeof editor === 'object' && editor.state
    ? editor.state.config
    : null;
  const controls = editor && typeof editor === 'object' && editor.controls
    ? editor.controls
    : null;
  const currentScreen = controls?.currentScreen;
  const setCurrentScreen = controls?.setCurrentScreen;
  const screenValues = React.useMemo(() => {
    const viewScreens = editorConfig?.search?.views?.[view]?.screens;
    return viewScreens ? Object.keys(viewScreens) : DEFAULT_SCREEN_VALUES;
  }, [editorConfig, view]);
  const screens = React.useMemo(() => screenValues.map((screen) => ({
    value: screen,
    label: SCREEN_LABELS[screen] ? <IntlMessages id={SCREEN_LABELS[screen]} /> : screen,
  })), [screenValues]);
  const screenSetKey = React.useMemo(() => `${view}:${screenValues.join('|')}`, [screenValues, view]);
  const initializedScreenSetRef = React.useRef(null);

  React.useEffect(() => {
    if (onRegisterSave && registeredSave) {
      onRegisterSave(registeredSave);
    }
  }, [onRegisterSave, registeredSave]);

  React.useEffect(() => {
    if (onRegisterReload && registeredReload) {
      onRegisterReload(registeredReload);
    }

    return () => {
      if (onRegisterReload) {
        onRegisterReload(null);
      }
    };
  }, [onRegisterReload, registeredReload]);

  React.useEffect(() => {
    if (!setCurrentScreen || screenValues.length === 0) {
      return;
    }

    const preferredScreen = getPreferredScreen(screenValues);
    const isNewScreenSet = initializedScreenSetRef.current !== screenSetKey;
    initializedScreenSetRef.current = screenSetKey;

    if (isNewScreenSet && preferredScreen && currentScreen !== preferredScreen) {
      setCurrentScreen(preferredScreen);
      return;
    }

    if (!screenValues.includes(currentScreen) && preferredScreen) {
      setCurrentScreen(preferredScreen);
    }
  }, [currentScreen, screenSetKey, screenValues, setCurrentScreen]);

  if (typeof editor !== 'object' || !editor.settingsPanel) {
    return editor;
  }

  const { settingsPanel, previewPanel } = editor;

  return (
    <ConfigStepLayout
      title={title}
      description={description}
      currentScreen={controls.currentScreen}
      currentDevice={controls.currentDevice}
      screens={screens}
      onScreenChange={controls.setCurrentScreen}
      onDeviceChange={controls.setCurrentDevice}
      settingsPanel={settingsPanel}
      previewPanel={previewPanel}
      isExpanded={isExpanded}
      onExpandToggle={onExpandToggle}
    />
  );
};

ConfigStep.propTypes = {
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  onRegisterReload: PropTypes.func,
  onUpdate: PropTypes.func,
  shopId: PropTypes.string.isRequired,
  stepId: PropTypes.string,
  experienceType: PropTypes.string,
};

ConfigStep.defaultProps = {
  onReady: null,
  onRegisterSave: null,
  onRegisterReload: null,
  onUpdate: null,
  stepId: null,
  experienceType: null,
};

export default ConfigStep;
